/**
 * The platform seams `@baton/core` reaches the host through. Core has no DOM dependency
 * and no web API beyond `fetch` (`plan.md` § Structure decision), so the event source, the
 * online signal, the key-value store, the upload sender, the focus signal and the tab
 * coordination are injected: `apps/web/src/platform/browser.ts` installs the browser ones over
 * `fetch`, `navigator.onLine`, `localStorage`, `XMLHttpRequest`, the window focus event, the Web
 * Locks API and `BroadcastChannel`, and feature 016 installs the native ones.
 */

export interface StreamMessage {
  readonly id: string | null;
  readonly type: string;
  readonly data: string;
}

export interface StreamConnection {
  close(): void;
}

export interface StreamHandlers {
  readonly onOpen: () => void;
  readonly onMessage: (message: StreamMessage) => void;
  /** A comment line (`: keepalive`) the host surfaces so the dead-connection timer resets. */
  readonly onKeepalive: () => void;
  readonly onError: () => void;
  /**
   * The instance answered 429. Not a failure, since the answer proves the instance is up.
   * `retryAfterMs` is the response's `Retry-After`, null when it carried none, and `cause` which
   * refusal it was.
   */
  readonly onRefused: (retryAfterMs: number | null, cause: RefusalCause) => void;
  /**
   * The instance answered that it has no such stream (404) or does not serve it (501), which no retry
   * changes: a reader that sets this stops on it, and one that does not hears `onError` as before.
   */
  readonly onUnavailable?: (status: number) => void;
}

/**
 * Which 429 refused a stream (2026-10-14): `stream_limit` when the problem's `reason` says the actor
 * already holds the most streams 001 FR-149 admits, and `rate_limit` for any other 429, a request
 * budget of `docs/OPERATIONS.md` § 8 or a body that says nothing.
 */
export type RefusalCause = "stream_limit" | "rate_limit";

/**
 * A stream opened by a `POST` whose answer is the event stream itself: 019's turn start
 * (`POST …/threads/{id}/turns` with `Accept: text/event-stream`, 019 `contracts/http-api.md` § Turns).
 * The body is the JSON already serialised and the headers are the ones the API client forms for a
 * mutation (`@baton/api`'s `streamRequestHeaders`), so this seam stays the host's transport and
 * nothing else. `onResponse` sees a non-stream answer before the handlers do: the 202 that queued the
 * message instead of streaming, and every problem, each read whole as JSON.
 */
export interface StreamRequest {
  readonly method: "POST";
  readonly body: string;
  readonly headers: Readonly<Record<string, string>>;
  /** An answer that is not the stream: its status and its JSON body, or null when the body was not JSON. */
  readonly onResponse: (status: number, body: unknown) => void;
}

export interface StreamSource {
  /** Open a server-sent event stream at `url`, sending `Last-Event-ID` when a cursor is given; with `request`, open it by that `POST`. */
  open(url: string, lastEventId: string | null, handlers: StreamHandlers, request?: StreamRequest): StreamConnection;
}

export interface OnlineSignal {
  readonly online: boolean;
  subscribe(listener: (online: boolean) => void): () => void;
}

export interface FocusSignal {
  subscribe(listener: () => void): () => void;
}

export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  keys(): readonly string[];
}

export interface UploadProgress {
  readonly loaded: number;
  readonly total: number;
}

export interface UploadRequest {
  readonly url: string;
  readonly method: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: Blob;
  readonly onProgress: (progress: UploadProgress) => void;
  readonly signal: AbortSignal;
}

export interface UploadResult {
  readonly status: number;
  readonly headers: Readonly<Record<string, string>>;
}

/** The browser branch's sender (`XMLHttpRequest` for its progress events) or the desktop
 * shell's `files:upload` over IPC; `uploads/where.ts` chooses between them. */
export interface UploadSender {
  send(request: UploadRequest): Promise<UploadResult>;
}

/** The desktop adapter's report of whether a shell hosts this code (017 `contracts/ipc.md` § Transport). */
export interface ShellReport {
  readonly hasShell: boolean;
  readonly shellUploader: UploadSender | null;
}

export interface Platform {
  readonly stream: StreamSource;
  readonly online: OnlineSignal;
  readonly focus: FocusSignal;
  readonly storage: KeyValueStore;
  readonly upload: UploadSender;
  readonly shell: ShellReport;
  readonly hashSha256: (blob: Blob) => Promise<string | null>;
  /**
   * Load the coordination between the tabs of one origin that lets them share one event stream
   * (`contracts/client-api.md` § Event stream, 2026-10-14). A loader, so only the stream engine, itself
   * a lazy chunk, carries it. Resolves null on a host without the Web Locks API or `BroadcastChannel`,
   * and is absent on a platform that never has them; in both cases each tab holds its own stream.
   */
  readonly loadTabs?: () => Promise<TabCoordination | null>;
}

/**
 * An exclusive lock and a broadcast channel scoped to the origin, which is what leader election
 * between tabs needs: the lock names the one tab that holds a resource, and the browser releases it
 * when that tab closes or crashes, so the next waiting tab is granted it without a heartbeat.
 */
export interface TabCoordination {
  /**
   * Wait for the exclusive lock `name` and hold it until the promise `whileHeld` returns settles.
   * Aborting `options.signal` withdraws a request that has not been granted; `options.steal` takes
   * the lock from its holder at once, whose own request then rejects. Resolves once the lock is
   * released; rejects when the request was withdrawn or refused, or the lock was stolen from it.
   */
  lock(name: string, options: LockOptions, whileHeld: () => Promise<void>): Promise<void>;
  /** Join the broadcast channel `name`, which every other tab of the origin that joined it hears. Throws where the host refuses one. */
  channel(name: string): TabChannel;
  /** Whether this tab is showing. Only a showing tab takes over from a leader that has gone silent. */
  visible(): boolean;
}

export type LockOptions = { readonly signal: AbortSignal; readonly steal?: never } | { readonly steal: true; readonly signal?: never };

export interface TabChannel {
  /** Send a structured-clonable message to the channel's other members; the sender does not hear it. */
  post(message: unknown): void;
  subscribe(listener: (message: unknown) => void): () => void;
  close(): void;
}

/** An in-memory key-value store: the test double and the fallback for a host whose storage throws. */
export function memoryStore(): KeyValueStore {
  const map = new Map<string, string>();
  return {
    get: (key) => map.get(key) ?? null,
    set: (key, value) => void map.set(key, value),
    remove: (key) => void map.delete(key),
    keys: () => [...map.keys()],
  };
}
